import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, List,
  ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';

import api from '../../../../services/api';
import SeletorDeHosts from './SeletorDeHosts';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { SUPERFICIE } from './estilo';

const ROTA = '/admin/snapshot';

/**
 * Criacao de grupo: nome, pai e HOSTS numa tela so.
 *
 * Antes eram dois passos — criar o grupo, depois abrir outra tela e vincular os
 * hosts. Se o segundo passo falhasse, ou o operador fechasse a tela no meio,
 * sobrava um grupo vazio que ninguem sabia se era intencional ou lixo. Aqui o
 * grupo so existe se existir por inteiro: o servidor grava tudo numa transacao,
 * e um host invalido impede a criacao em vez de deixar meio feito.
 */
export default function GrupoDialog({ aberto, empresa, hosts, grupos, paiSugerido, onFechar, onCriado }) {
  const notify = useNotify();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [pai, setPai] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState({});

  useEffect(() => {
    if (!aberto) {
      setNome(''); setDescricao(''); setPai(null);
      setSelecionados([]); setErros({});
      return;
    }
    setPai(paiSugerido || null);
  }, [aberto, paiSugerido]);

  const criar = async () => {
    setSalvando(true);
    setErros({});
    try {
      const res = await api.post(`${ROTA}/group/create`, {
        empresa,
        name: nome.trim(),
        description: descricao.trim() || null,
        parent_id: pai?.id || null,
        host_ids: selecionados,
      });

      if (res.data?.errors?.length) {
        const porCampo = {};
        res.data.errors.forEach((e) => { porCampo[e.field] = e.msg; });
        setErros(porCampo);
        return;
      }

      const d = res.data.data;
      notify.setNotify({
        open: true,
        severity: 'success',
        message: `Grupo “${d.name}” criado com ${d.host_count} host(s)`,
      });
      onCriado?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao criar o grupo' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="md" fullWidth PaperProps={{ sx: SUPERFICIE }}>
      <DialogTitle sx={{ pb: 1 }}>
        Novo grupo
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
          Nome e equipamentos de uma vez
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            autoFocus size="small" label="Nome do grupo" sx={{ flex: 1 }}
            placeholder="Ex.: POP Sul — borda"
            value={nome} onChange={(e) => { setNome(e.target.value); setErros((x) => ({ ...x, name: null })); }}
            error={!!erros.name} helperText={erros.name || ' '}
          />
          <Autocomplete
            sx={{ flex: 1 }}
            options={grupos}
            value={pai}
            onChange={(_, v) => setPai(v)}
            getOptionLabel={(o) => o.label || o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => (
              <TextField {...p} size="small" label="Dentro de (opcional)"
                helperText={erros.parent_id || 'Vazio = grupo raiz'}
                error={!!erros.parent_id} />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <FolderIcon sx={{ fontSize: 15, mr: 1, color: '#94a3b8' }} />
                <span style={{ fontFamily: 'monospace' }}>{o.label}</span>
              </li>
            )}
          />
        </Stack>

        <TextField
          fullWidth size="small" label="Descrição (opcional)" sx={{ mb: 2 }}
          value={descricao} onChange={(e) => setDescricao(e.target.value)}
        />

        <Divider sx={{ mb: 1.5 }} />

        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Equipamentos</Typography>

        {erros.host_ids && <Alert severity="error" sx={{ mb: 1 }}>{erros.host_ids}</Alert>}

        <SeletorDeHosts
          empresa={empresa}
          selecionados={selecionados}
          aoMudar={(ids) => { setSelecionados(ids); setErros((e) => ({ ...e, host_ids: null })); }}
        />

        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
          Dá para criar sem nenhum e adicionar depois — mas o normal é já sair pronto.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" disabled={!nome.trim() || salvando} onClick={criar}>
          {salvando ? 'Criando…' : `Criar com ${selecionados.length} host(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Confirmacao de remocao.
 *
 * Grupo com filhos nao tem resposta unica, entao a decisao e do operador e vem
 * explicita. Apagar a subarvore em silencio destruiria trabalho que ninguem
 * pediu para destruir.
 */
export function RemoverGrupoDialog({ grupo, empresa, onFechar, onRemovido }) {
  const notify = useNotify();
  const [modo, setModo] = useState('reparent');
  const [removendo, setRemovendo] = useState(false);

  useEffect(() => { setModo('reparent'); }, [grupo]);

  if (!grupo) return null;

  const temFilhos = (grupo.children || []).length > 0;

  const remover = async () => {
    setRemovendo(true);
    try {
      const res = await api.post(`${ROTA}/group/${grupo.id}/delete`, {
        empresa, children: temFilhos ? modo : 'reparent',
      });
      if (res.data?.error || res.data?.errors) {
        notify.setNotify({
          open: true, severity: 'error',
          message: res.data.error || res.data.errors[0].msg,
        });
        return;
      }
      notify.setNotify({
        open: true, severity: 'success',
        message: `${res.data.data.removed} grupo(s) removido(s)`,
      });
      onRemovido?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao remover' });
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Dialog open={!!grupo} onClose={onFechar} maxWidth="xs" fullWidth PaperProps={{ sx: SUPERFICIE }}>
      <DialogTitle>Remover grupo</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{grupo.name}</Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Os <strong>equipamentos não são apagados</strong> — só o vínculo com o
          grupo. Grupo é uma forma de organizar, não dono do host.
        </Alert>

        {temFilhos && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Este grupo tem {grupo.children.length} subgrupo(s). O que fazer com eles?
            </Typography>
            <Stack spacing={1}>
              {[
                { v: 'reparent', t: 'Subir um nível', d: 'Os subgrupos passam a pendurar no pai deste.' },
                { v: 'cascade', t: 'Apagar junto', d: 'A subárvore inteira é removida.' },
              ].map((o) => (
                <Box
                  key={o.v}
                  onClick={() => setModo(o.v)}
                  sx={{
                    p: 1.25, borderRadius: 1, cursor: 'pointer',
                    border: `2px solid ${modo === o.v ? '#2563eb' : '#e2e8f0'}`,
                    bgcolor: modo === o.v ? '#eff6ff' : 'transparent',
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{o.t}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>{o.d}</Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {!temFilhos && (
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Sem subgrupos. Só este grupo é removido.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button color="error" variant="contained" onClick={remover} disabled={removendo}>
          {removendo ? 'Removendo…' : 'Remover'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
